import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
  FormGroup,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { formatDate } from './utils/dateUtils';
import { useUser } from './UserContext';

const CycleNotesModule = ({ elementType, elementId }) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [error, setError] = useState('');
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [noteToDelete, setNoteToDelete] = useState(null);
  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const { user } = useUser();

  useEffect(() => {
    fetchCycleNotes();
    fetchUsers();
  }, [elementType, elementId]);

  const fetchCycleNotes = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/ciclos/${elementType}/${elementId}/notas`);
      setNotes(response.data);
    } catch (err) {
      console.error('Failed to fetch cycle notes', err);
      setError('No se pudieron cargar las notas del ciclo.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/users/for-notifications');
      setUsers(response.data);
    } catch (err) {
      console.error('Failed to fetch users', err);
    }
  };

  const canEditDelete = (note) => {
    if (!user) return false;
    if (user.role === 'ADMIN' || user.role === 'SUPERADMIN') return true;
    return note.user_id === user.id;
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      setError('La nota no puede estar vacía.');
      return;
    }
    setError('');

    const payload = {
      contenido: newNote,
      [`${elementType}_id`]: elementId,
    };

    try {
      const response = await axios.post('/api/notas', payload);
      setNotes([response.data, ...notes]);
      setNewNote('');
    } catch (err) {
      setError('No se pudo añadir la nota.');
      console.error(err);
    }
  };

  const handleAddNoteWithNotify = () => {
    if (!newNote.trim()) {
      setError('La nota no puede estar vacía.');
      return;
    }
    setError('');
    setNotifyDialogOpen(true);
  };

  const handleSendNotification = async () => {
    const payload = {
      contenido: newNote,
      [`${elementType}_id`]: elementId,
      notified_user_ids: selectedUserIds,
    };

    try {
      const response = await axios.post('/api/notas/notify', payload);
      setNotes([response.data, ...notes]);
      setNewNote('');
      setSelectedUserIds([]);
      setNotifyDialogOpen(false);
    } catch (err) {
      setError('No se pudo añadir la nota con notificación.');
      console.error(err);
    }
  };

  const handleEditClick = (note) => {
    setEditingNoteId(note.id);
    setEditContent(note.contenido);
  };

  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditContent('');
  };

  const handleSaveEdit = async (noteId) => {
    try {
      const response = await axios.put(`/api/notas/${noteId}`, {
        contenido: editContent,
      });
      setNotes(notes.map((n) => (n.id === noteId ? response.data : n)));
      setEditingNoteId(null);
      setEditContent('');
    } catch (err) {
      setError('No se pudo editar la nota.');
      console.error(err);
    }
  };

  const handleDeleteClick = (note) => {
    setNoteToDelete(note);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`/api/notas/${noteToDelete.id}`);
      setNotes(notes.filter((n) => n.id !== noteToDelete.id));
      setDeleteDialogOpen(false);
      setNoteToDelete(null);
    } catch (err) {
      setError('No se pudo eliminar la nota.');
      console.error(err);
      setDeleteDialogOpen(false);
    }
  };

  const handleUserToggle = (userId) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Notas del Ciclo
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {/* Notes List */}
      <List dense>
        {notes.map((note) => (
          <ListItem
            key={note.id}
            sx={{
              bgcolor: 'background.paper',
              mb: 1,
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
            secondaryAction={
              canEditDelete(note) && (
                <Box>
                  {editingNoteId === note.id ? (
                    <>
                      <IconButton
                        edge="end"
                        aria-label="save"
                        onClick={() => handleSaveEdit(note.id)}
                        color="primary"
                        size="small"
                      >
                        <SaveIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="cancel"
                        onClick={handleCancelEdit}
                        size="small"
                      >
                        <CancelIcon fontSize="small" />
                      </IconButton>
                    </>
                  ) : (
                    <>
                      <IconButton
                        edge="end"
                        aria-label="edit"
                        onClick={() => handleEditClick(note)}
                        size="small"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        edge="end"
                        aria-label="delete"
                        onClick={() => handleDeleteClick(note)}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </>
                  )}
                </Box>
              )
            }
          >
            {editingNoteId === note.id ? (
              <TextField
                fullWidth
                multiline
                value={editContent}
                onChange={(e) => setEditContent(e.target.value)}
                variant="outlined"
                size="small"
              />
            ) : (
              <ListItemText
                primary={note.contenido}
                secondary={`${formatDate(note.created_at)} - ${note.user?.full_name || note.user?.username || 'Usuario'}`}
                primaryTypographyProps={{ fontSize: '0.9rem' }}
                secondaryTypographyProps={{ fontSize: '0.75rem' }}
              />
            )}
          </ListItem>
        ))}
        {notes.length === 0 && (
          <Typography sx={{ fontStyle: 'italic', color: 'text.secondary', fontSize: '0.9rem' }}>
            No hay notas en este ciclo.
          </Typography>
        )}
      </List>

      {/* Add Note Section */}
      <Box sx={{ mt: 2 }}>
        <TextField
          label="Añadir nueva nota"
          value={newNote}
          onChange={(e) => setNewNote(e.target.value)}
          fullWidth
          multiline
          rows={3}
          variant="outlined"
          size="small"
        />
        <Box sx={{ mt: 1, display: 'flex', gap: 1 }}>
          <Button variant="contained" onClick={handleAddNote} size="small">
            Añadir
          </Button>
          <Button variant="contained" color="secondary" onClick={handleAddNoteWithNotify} size="small">
            Añadir y Notificar
          </Button>
        </Box>
        {error && (
          <Typography color="error" sx={{ mt: 1, fontSize: '0.85rem' }}>
            {error}
          </Typography>
        )}
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <Typography>¿Estás seguro de que deseas eliminar esta nota?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification User Selection Dialog */}
      <Dialog open={notifyDialogOpen} onClose={() => setNotifyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Seleccionar Usuarios a Notificar</DialogTitle>
        <DialogContent>
          <FormGroup>
            {users.map((u) => (
              <FormControlLabel
                key={u.id}
                control={
                  <Checkbox
                    checked={selectedUserIds.includes(u.id)}
                    onChange={() => handleUserToggle(u.id)}
                  />
                }
                label={`${u.full_name || u.username} (${u.role})`}
              />
            ))}
          </FormGroup>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotifyDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleSendNotification}
            color="primary"
            variant="contained"
            disabled={selectedUserIds.length === 0}
          >
            Enviar Notificación
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CycleNotesModule;
